import { Modal } from 'antd'
import { Button } from 'common/components/Button/Button'
import { Select } from 'common/components/Select/Select'
import { BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import dayjs from 'dayjs'
import { useEffect, useState, type FC } from 'react'
import { useLazyFetchPatientEncountersQuery } from './transactionsMapping.api'
import styles from './transactionsMapping.module.scss'

type Props = {
  isOpen: boolean
  onClose: () => void
  patientId?: number | null
  errorMessage?: string
  isResolving?: boolean
  onConfirm: (payload: { encounterId: number; encounterUuid: string }) => void
}

export const EncounterSelectModal: FC<Props> = ({
  isOpen,
  onClose,
  patientId,
  errorMessage,
  isResolving,
  onConfirm,
}) => {
  const [selectedEncounter, setSelectedEncounter] = useState<number | null>(null)
  const [fetchEncounters, { data: encountersData, isFetching: isLoadingEncounters }] =
    useLazyFetchPatientEncountersQuery()

  useEffect(() => {
    if (isOpen && patientId) {
      setSelectedEncounter(null)
      fetchEncounters({ patientId })
    }
    if (!isOpen) {
      setSelectedEncounter(null)
    }
  }, [isOpen, patientId])

  const encounters = Array.isArray(encountersData?.encounters?.data)
    ? encountersData.encounters.data
    : []

  const handleConfirm = () => {
    const encounter = encounters.find((e) => e.eid === selectedEncounter)
    if (selectedEncounter && encounter) {
      onConfirm({ encounterId: selectedEncounter, encounterUuid: encounter.euuid })
    }
  }

  return (
    <Modal
      title='Select Encounter'
      open={isOpen}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width='480px'>
      {errorMessage && <p className={styles.modalError}>{errorMessage}</p>}
      <Select
        label='Select Encounter'
        isLoading={isLoadingEncounters}
        listOptions={encounters.map((e) => ({
          code: e.eid,
          name: e.reason
            ? `${dayjs(e.date).format('MM-DD-YYYY')} - ${e.reason}`
            : dayjs(e.date).format('MM-DD-YYYY'),
        }))}
        value={selectedEncounter ?? undefined}
        onChange={(val) => setSelectedEncounter(val)}
        placeholder='Select an encounter'
        required
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
        <Button onClick={onClose} modifier={BUTTON_MODIFIER.SECONDARY}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!selectedEncounter || isResolving}
          modifier={BUTTON_MODIFIER.PRIMARY}>
          Retry
        </Button>
      </div>
    </Modal>
  )
}
